// App API Ajax calls location
import api from '../api'
import router from '../router'
import config from '../config/index'
import * as Routes from '../constants/routes'
import * as Environments from '../constants/environments'
import { APIEvent } from '../events/apiEvent'
import { EventBus } from '@/event-bus.js'

const useAuthHelpers = () => {
  /**
   * Core authentication API call
   * @param {Object} credentials - User credentials
   * @returns {Promise} API response
   */
  const authenticateUser = (credentials) => {
    return api.request('post', 'User/Account/Login', credentials)
  }

  /**
   * Fetch user details by token
   * @param {Object} options - Request options
   * @returns {Promise} API response
   */
  const fetchUserByToken = (options) => {
    return api.request('post', 'User/Account/ReadUserByToken', options, { withCredentials: true })
  }

  /**
   * Establish user session after successful authentication
   * @param {Function} commit - Vuex commit function
   * @param {Object} data - Authentication response data
   * @returns {string} token - The authentication token
   */
  const establishUserSession = (commit, data) => {
    if (!data.token) {
      throw new Error('No authentication token received')
    }

    const token = data.token
    
    // Update Vuex state
    commit('addToken', token)
    
    // Clear temporary auth data
    commit('removeUsername')
    commit('removePassword')
    commit('removeOTPauth')
    commit('removeSecret')
    
    // Set session in browser
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('token', token)
      window.localStorage.setItem('roleID', data.user_role_id)
      window.localStorage.setItem('tenantID', data.tenant_id)
    }
    
    return token
  }

  /**
   * Setup 2FA configuration
   * @param {Function} commit - Vuex commit function
   * @param {Object} data - Response data containing secKey
   * @param {Object} credentials - User credentials
   * @returns {string} otpauth - The OTP auth URL
   */
  const setup2FA = (commit, data, credentials) => {
    if (!data.secKey) {
      throw new Error('No security key provided for 2FA setup')
    }

    commit('addSecret', data.secKey)
    const otpauth = `otpauth://totp/LeakBot${config.environment}(${credentials.username})?secret=${data.secKey}`
    commit('addOTPauth', otpauth)
    return otpauth
  }

  /**
   * Handle post-authentication navigation
   * @param {Object} data - Response data
   */
  const handlePostAuthNavigation = (data) => {
    if (data.redirectURL) {
      // Using window.location for external redirects
      window.location.href = data.redirectURL
    } else {
      // Using Vue Router for internal navigation
      router.push(Routes.HOME)
    }
  }

  /**
   * Generate dynamics options object
   * @param {Object} credentials - User credentials
   * @returns {Object} dynamics options
   */
  const getDynamicsOptions = (credentials) => {
    return {
      dynamics: credentials.dynamics === 'yes' ? 'yes' : 'no'
    }
  }

  return {
    authenticateUser,
    fetchUserByToken,
    establishUserSession,
    setup2FA,
    handlePostAuthNavigation,
    getDynamicsOptions
  }
}

// Initialize auth helpers
// Set up similar to Vue 3 composable pattern, so we can move this at a later date
const authHelpers = useAuthHelpers()

export default {
  /**
   * Handle user login - supports both regular login and 2FA flows
   */
  [APIEvent.LOGIN]: async ({ commit, dispatch }, credentials) => {
    // Input validation
    if (!credentials || !credentials.username) {
      throw new Error('Invalid credentials provided')
    }

    try {
      const response = await authHelpers.authenticateUser(credentials)
      const data = response.data

      // Store credentials temporarily for potential 2FA verification
      commit('addUsername', credentials.username)
      commit('addPassword', credentials.password)

      // Handle 2FA setup flow (new user)
      if (data.secKey) {
        authHelpers.setup2FA(commit, data, credentials)
        await router.push(Routes.QR_CODE)
        return { 
          success: true, 
          requires2FA: true, 
          type: 'setup',
          message: '2FA setup required'
        }
      }

      // Handle existing 2FA verification flow
      if (data.ack) {
        await router.push(Routes.ONE_TIME_PWD)
        return { 
          success: true, 
          requires2FA: true, 
          type: 'verify',
          message: '2FA verification required'
        }
      }

      // No 2FA required, complete login immediately
      const otpResult = await dispatch(APIEvent.CHECK_OTP, credentials)
      return {
        success: true,
        requires2FA: false,
        token: otpResult.token,
        message: 'Login successful'
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed'
      throw new Error(`Authentication failed: ${errorMessage}`)
    }
  },

  /**
   * Verify OTP and complete authentication
   */
  [APIEvent.CHECK_OTP]: async ({ commit }, credentials) => {
    try {
      const response = await authHelpers.authenticateUser(credentials)
      const data = response.data

      // Establish user session
      const token = authHelpers.establishUserSession(commit, data)

      // Fetch additional user details
      const dynamicsOptions = authHelpers.getDynamicsOptions(credentials)
      
      try {
        const userResponse = await authHelpers.fetchUserByToken(dynamicsOptions)
        commit('addRoleID', userResponse.data.XaUserRole_ID)
      } catch (userError) {
        console.warn('Failed to fetch user role:', userError)
      }

      authHelpers.handlePostAuthNavigation(data)

      return { 
        success: true, 
        token,
        message: 'Authentication completed'
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'OTP verification failed'
      throw new Error(`OTP verification failed: ${errorMessage}`)
    }
  },

  [APIEvent.SSO_LOGIN]: async ({ dispatch }, { targetPath = '/' }) => {
    try {
      // Use the new Auth0 action to handle login
      await dispatch('AUTH0_LOGIN', {
        appState: { targetPath }
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'SSO login failed'
      throw new Error(`SSO Login failed: ${errorMessage}`)
    }
  },

  [APIEvent.EXCHANGE_ID_TOKEN]: async ({ commit, state }) => {
    try {
      const auth0 = state.auth0Client;
      
      if (!auth0 || typeof auth0.getIdTokenClaims !== 'function') {
        console.error('Auth0 client invalid:', auth0);
        throw new Error('[EXCHANGE_ID_TOKEN] Invalid Auth0 client');
      }

      const idTokenClaims = await auth0.getIdTokenClaims();
      
      if (!idTokenClaims || !idTokenClaims.__raw) {
        console.error('No valid ID token claims found');
        throw new Error('[EXCHANGE_ID_TOKEN] No valid ID token claims');
      }
      
      const idToken = idTokenClaims.__raw;

      // Call our backend API
      const response = await api.request('post', 'User/Account/TokenExchange', { 'id_token': idToken});
      const data = response.data;

      if (data.token) {
        commit('addToken', data.token);

        // Store it (localStorage, Vuex, etc.)
        window.localStorage.setItem('token', data.token);
        window.localStorage.setItem('roleID', data.user_role_id);
        window.localStorage.setItem('tenantID', data.tenant_id);
        
        // On success, store the returned token and roleID, then redirect
        const options = {dynamics: "no"};
        const userResponse = await api.request('post', 'User/Account/ReadUserByToken', options, {withCredentials: true});
        
        window.localStorage.setItem('roleID', userResponse.data.XaUserRole_ID);
        window.localStorage.setItem('tenantID', userResponse.data.XaTenant_ID);
        commit('addRoleID', userResponse.data.XaUserRole_ID);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'SSO login failed'
      throw new Error(`SSO Login failed: ${errorMessage}`)
    }
  },


  [APIEvent.FETCH_TOKEN]: (_, params) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/ReadUserByToken', params)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.RESET_2FA]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/ResetTFA', options)
        .then(() => {
          resolve()
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.RESET_PWD]: ({commit}, username) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/ResetPassword', {username, 'app_type': 'insurer_dashboard'})
        .then(() => {
          commit('SET_USERNAME', JSON.stringify(username))
          // commit('removeUsername')
          commit('removePassword')
          commit('removeOTPauth')
          commit('removeSecret')
          resolve()
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.CHANGE_PWD]: (_, params) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/ChangePassword', params)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.LOGOUT]: async ({ commit, state, dispatch }) => {
    // Clear Auth0 state first
    commit('SET_AUTH0_CLIENT', null)
    commit('SET_AUTH0_AUTHENTICATED', false)
    commit('SET_AUTH0_USER', {})
    commit('SET_AUTH0_ERROR', null)
    
    // Clear other app state
    commit('removeToken')
    commit('SET_SEARCHRESULTS', [])
    commit('SET_MESSAGE_LIST', null)
    commit('SET_LANGUAGE_LIST', [])
    commit('SET_TENANTS_LIST', [])
    commit('SET_FIRST_TENANT', {})
    commit('SET_DEVICES_LIST', [])
    commit('SET_LEAKLIST', [])
    commit('SET_API_LIST', [])
    commit('addRoleID', null)
    // commit('addCookie', null)
    commit('SET_TENANT_GROUPS', [])
    commit('SET_USER_LIST', [])
    commit('SET_CURRENT_DEVICE', null)
    commit('dynamicsLogin', null)
    // commit('resetState')
    
    return new Promise(async (resolve, reject) => {
      try {
        // Logout from Auth0 if client exists
        if (state.auth0Client) {
          try {
            await dispatch('AUTH0_LOGOUT', {
              logoutParams: {
                returnTo: window.location.origin + '/login'
              }
            })
          } catch (auth0Error) {
            console.warn('Auth0 logout failed:', auth0Error)
            // Continue with regular logout even if Auth0 logout fails
          }
        }
        
        // Load data from server
        // Note: you cannot commit here, the data is not available yet
        api.request('post', 'User/Account/Logout', {})
          .then(response => {
            if (response.data.ack) {
              window.localStorage.clear()
              router.push(Routes.LOGIN)
            }
            resolve(response)
          }, (error) => {
            // error in loading data
            if (error.error === '0' || error.error === '52') {
              console.log(error)
            } else {
              reject(error)
            }
          })
        window.localStorage.clear()
        location.reload(true)
      } catch (error) {
        console.error('Logout error:', error)
        // Even if there's an error, clear local storage and redirect
        window.localStorage.clear()
        router.push(Routes.LOGIN)
        resolve()
      }
    })
  },

  SET_TOKEN ({commit}, token) {
    commit('SET_TOKEN', token)
  },

  SET_USER ({commit}, user) {
    commit('SET_USER', user)
  },

  [APIEvent.SEARCH]: ({commit}, params) => {
    return new Promise((resolve, reject) => {
      var apiURL = 'Device/Device/SearchByCustomerDetails'
      var data = {}
      if (params.searchType === 'DeviceName' || params.searchType === 'LeakBotId') {
        apiURL = 'Device/Device/Search'
        data.type = params.searchType
        data.id = params.searchString
      } else {
        data[params.searchType] = params.searchString
        data.start_offset = typeof params.startOffset !== 'undefined'
          ? params.startOffset.toString()
          : '0'
        data.fetch_size = typeof params.perPage !== 'undefined'
          ? params.perPage.toString()
          : '20'
      }
      api.request('post', apiURL, data)
        .then(res => {
          var results
          if (res.data.list) {
            results = res.data.list.record
            var hasNextPage = res.data.LastPage === '0'
            commit('SET_HASNEXTPAGE', hasNextPage)
            commit('SET_SEARCHRESULTS', results)
          } else if (res.data.Device) {
            results = [res.data.Device]
            commit('SET_SEARCHRESULTS', results)
          }
          if (typeof results === 'undefined') {
            // no matches throw an error to inform UI
            throw new Error('No matches found')
          }
          resolve(results, hasNextPage)
        })
        .catch((error) => {
          commit('SET_SEARCHRESULTS', [])
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_DONUTS]: ({ commit }) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      api.request('post', 'LbReport/Report/DeviceTelemetry', {}, {withCredentials: true}).then(response => {
        // The data is available now. Finally we can commit something
        var resAll = response.data.report.device
        var res = response.data.report.device[0]
        commit('setDonutsData', res)
        commit('setAllDonutsData', resAll)
        commit('SET_K0', res['v0'])
        commit('SET_K1', res['v1'])
        commit('SET_K2', res['v2'])
        commit('SET_K3', res['v3'])
        commit('SET_K4', res['v4'])
        commit('SET_K5', res['v5'])
        commit('SET_K6', res['v6'])
        commit('SET_K7', res['v7'])
        commit('SET_K8', res['v8'])
        commit('SET_K9', res['v9'])
        commit('SET_K10', res['v10'])
        commit('SET_K11', res['v11'])
        commit('SET_K12', res['v12'])
        commit('SET_K13', res['v13'])
        resolve()
      }, (error) => {
        // error in loading data
        reject(error)
      })
    })
  },

  [APIEvent.FETCH_INSTALLS_DONUT]: ({ commit }) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      api.request('post', 'LeakCentral/Ticket/GetDevicesStatistics/', {}, {withCredentials: true}).then(response => {
        commit('setInstallsDonut', response.data)
        commit('SET_Installed', response.data.Installed)
        commit('SET_LeakActive', response.data.LeakActive)
        commit('SET_LeakInactive', response.data.LeakInactive)
        commit('SET_Learning', response.data.Learning)
        resolve()
      }, (error) => {
        // error in loading data
        reject(error)
      })
    })
  },

  [APIEvent.FETCH_TENANTS]: ({ commit }) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/List/', {})
        .then((response) => {
          commit('SET_TENANTS_LIST', response.data.tenants)
          commit('SET_FIRST_TENANT', response.data.tenants[0].name)
          resolve(response)
        })
        .catch((error) => {
          if (error) {
            switch (error) {
              case 52:
                EventBus.$emit('TOKEN is not valid')
                break
              case 61:
                EventBus.$emit('invalid-token')
                break
              default:
                break
            }
            reject(error)
          }
          // reject(error)
        })
    })
  },

  [APIEvent.FETCH_CAMPAIGN_ID]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'ECommerce/Order/GetCampaignId/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_TENANT_INFO]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/View/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.UPDATE_TENANT_INFO]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/Update/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.ADD_TENANT]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/Create/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_TENANT]: (_, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      api.request('post', 'User/Tenant/View/', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.UPDATE_USER_TENANT_GROUP]: (_, data) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/UpdateUserTenantGroup/', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_LOW_LEAK_ALERTS]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/LowLeakAlerts/', data) : api.mock('LbReport/Report/LowLeakAlerts/'))
          .then(response => {
          // The data is available now. Finally we can commit something
            commit('SET_LOW_LEAK_ALERTS', response)  
            resolve(response)
          }, (error) => {
            reject(error)
          })
    })
  },

  [APIEvent.FETCH_REPAIRS]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/Repairs/', data) : api.mock('LbReport/Report/Repairs/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          resolve(response)
          commit('SET_REPAIRS', response)
        }, (error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_DAMAGE_CAUSING_REPAIRS]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/DamageCausingRepairs/', data) : api.mock('LbReport/Report/DamageCausingRepairs/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          resolve(response)
          commit('SET_DAMAGE_CAUSING_REPAIRS', response)
        }, (error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_LEAK_CATEGORIES]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/LeakCategories/', data) : api.mock('LbReport/Report/LeakCategories/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          resolve(response)
          commit('SET_LEAK_CATEGORIES', response)
        }, (error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_COST_OF_PROGRAMME]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/CostOfProgramme/', data) : api.mock('LbReport/Report/CostOfProgramme/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          resolve(response)
          commit('SET_COST_OF_PROGRAMME', response)
        }, (error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_WORK_ORDER_CLAIM_SAVINGS]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/WorkOrderClaimSavings/', data) : api.mock('LbReport/Report/WorkOrderClaimSavings/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          resolve(response)
          commit('SET_WORK_ORDER_CLAIM_SAVINGS', response)
        }, (error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_CUSTOMER_FEEDBACK]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/CustomerFeedback/', data) : api.mock('LbReport/Report/CustomerFeedback/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          resolve(response)
          commit('SET_CUSTOMER_FEEDBACK', response)
        }, (error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_MARKETING_METRICS]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/MarketingMetrics/', data) : api.mock('LbReport/Report/MarketingMetrics/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          resolve(response)
          commit('SET_MARKETING_METRICS', response)
        }, (error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_ORDERS_AND_REGISTRATIONS]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/OrdersAndRegistrations/', data) : api.mock('LbReport/Report/OrdersAndRegistrations/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          console.log('Handling the response')
          resolve(response)
          commit('SET_ORDERS_AND_REGISTRATIONS', response)
        }, (error) => {
          console.log('Error')
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_MONTHLY_REGISTRATIONS]: ({commit}, data) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      (Environments.ACTIVE_ENV !== 'QA' ? api.request('post', 'LbReport/Report/MonthlyRegistrations/', data) : api.mock('LbReport/Report/MonthlyRegistrations/'))
        .then(response => {
        // The data is available now. Finally we can commit something
          resolve(response)
          commit('SET_MONTHLY_REGISTRATIONS', response)
        }, (error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_DEVICES]: ({ commit }, options) => {
    // Return a promise so that calling method may show an AJAX spinner gif till this is done
    return new Promise((resolve, reject) => {
      // Load data from server
      // Note: you cannot commit here, the data is not available yet
      api.request('post', '/Device/Device/ListAdminFrontEnd', options, {withCredentials: true})
        .then(response => {
        // The data is available now. Finally we can commit something
          // console.log(response.data.list.record)
          resolve(response)
          commit('SET_DEVICES_LIST', response.data.list.record)
        }, (error) => {
          reject(error)
        })
    })
  },

  [APIEvent.UPDATE_DEVICE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Device/Update', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_LEAK_LIST]: (_, options) => {
    // defaults
    options.start_record = typeof options.start_record !== 'undefined'
      ? options.start_record.toString()
      : '0'
    options.fetch_size = typeof options.fetch_size !== 'undefined'
      ? options.fetch_size.toString()
      : '20'
    options.type_id = typeof options.type_id !== 'undefined'
      ? options.type_id.toString()
      : '1'
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Ticket/DerivedEventList', options)
        .then((response) => {
          // console.log(response)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_REMOVED_DEVICES_LIST]: (_, options) => {
    // defaults
    options.start_offset = typeof options.start_offset !== 'undefined'
      ? options.start_offset.toString()
      : '0'
    options.fetch_size = typeof options.fetch_size !== 'undefined'
      ? options.fetch_size.toString()
      : '20'
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Removed/Search', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_INCOMPLETE_INSTALLS_LIST]: ({ commit }, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Support/IncompleteInstall', options)
        .then((response) => {
          resolve(response)
          commit('setIncompleteInstallsList', response.data.list.records)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_EVENT_LIST]: (_, options) => {
    // defaults
    options.start_record = typeof options.start_record !== 'undefined'
      ? options.start_record.toString()
      : '0'
    options.fetch_size = typeof options.fetch_size !== 'undefined'
      ? options.fetch_size.toString()
      : '20'
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Ticket/DerivedEventList', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [APIEvent.FETCH_DEVICE]: ({commit}, id) => {
    let data = {id, type: 'LeakBotId'}
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Device/Search', data)
        .then((response) => {
          commit('SET_CURRENT_DEVICE', response.data)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_DERIVED_EVENTS]: (_, data) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Ticket/DerivedEventList', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_DERIVED_EVENTS_VIEW]: (_, data) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Ticket/SimpleDerivedEventList', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_ACCOUNT]: (_, accountId) => {
    let data = {account_id: accountId}
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/Read/', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.UPDATE_ACCOUNT]: (_, update) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/Update/', update)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.UPDATE_VIP]: (_, update) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/VipUpdate', update)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.UPDATE_ADDRESS]: (_, update) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Address/AddressUpdate/', update)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_NOTES]: (_, id) => {
    let data = {LbDevice_ID: id}
    return new Promise((resolve, reject) => {
      api.request('post', '/Device/AgentNotes/List/', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.ADD_NOTE]: (_, note) => {
    return new Promise((resolve, reject) => {
      api.request('post', '/Device/AgentNotes/Add/', note)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_DEVICE_MESSAGES]: (_, options) => {
    let data = {
      LbDevice_ID: options.id,
      start_offset: options.start_offset || '0',
      fetch_size: options.fetch_size || '10'
    }
    return new Promise((resolve, reject) => {
      api.request('post', '/Device/Device/ListMessagesForDevice', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_USER_COMMS]: (_, data) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Device/GetUserComs/', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.REMOVE_DEVICE_FROM_USER]: (_, data) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Device/RemoveFrom/', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_USER_EMAIL]: (_, id) => {
    let data = { comms_id: id }
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Device/GetUserComsDetails/', data)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_WATER_USAGE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Device/ListWaterUsage/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_ENG_BOOKING]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Booking/View/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.CREATE_ENG_BOOKING]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Booking/Create/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.EDIT_ENG_BOOKING]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Booking/Edit/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.CANCEL_ENG_BOOKING]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Booking/Cancel/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.MARK_REPAIRED]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Ticket/LeakRepaired/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_MESSAGE_LIST]: ({commit}, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Email/List', options)
        .then((response) => {
          commit('SET_MESSAGE_LIST', response.data.records)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_MESSAGE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Email/View', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_LANGUAGE_LIST]: ({commit}) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/LanguageList', {})
        .then((response) => {
          commit('SET_LANGUAGE_LIST', response.data.records)
          resolve()
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_MESSAGE_TYPES]: ({commit}) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Email/ListCommType', {})
        .then((response) => {
          commit('SET_MESSAGE_TYPES', response.data.records)
          resolve()
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.UPDATE_MESSAGE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Email/Update', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.ADD_MESSAGE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Email/Add', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_TENANT_GROUPS]: ({commit}, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/ListTenantGroup', options)
        .then((response) => {
          commit('SET_TENANT_GROUPS', response.data.list.record)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_GROUP_TENANTS]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/ListTenantGroupTenants', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.USER_TENANT_LIST]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/List', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.ADD_TENANT_GROUP]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/CreateTenantGroup', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.UPDATE_TENANT_GROUP]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/UpdateTenantGroup', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.ADD_TENANT_TO_GROUP]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/LinkTenantGroup', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.DELETE_TENANT_GROUP]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/DeleteTenantGroup', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.DELETE_TENANT_FROM_GROUP]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Tenant/UnLinkTenantGroup', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_USER_ROLES]: ({ commit }, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Role/List/', options)
        .then((response) => {
          commit('SET_USER_ROLES', response.data.list.role)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_USERS]: ({ commit }, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Role/ListUsers/', options)
        .then((response) => {
          commit('SET_USER_LIST', response.data.list.role)
          resolve(response)
        })
        .catch((error) => {
          if (error.error === 307) {
            commit('SET_USER_LIST', [])
          }
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_API]: () => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Configuration/ListApis/', {})
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_ROLE_API]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Configuration/ListApis/', options)
        .then((response) => {
          // commit('SET_ROLE_API_LIST', response.data.list.records)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_ROLE_APIS]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Configuration/ListRolesForApi/', options)
        .then((response) => {
          // commit('SET_ROLE_API_LIST', response.data.list.records)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.ADD_API_TO_ROLE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Configuration/GrantApi/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.ADD_ROLE_TO_API]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Configuration/GrantApi/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.REMOVE_API_FROM_ROLE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'LeakCentral/Configuration/RevokeApi/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.ADD_USER]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Account/Create/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.FETCH_PARAMETERS]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Device/ParameterList/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.MATLAB_PARAMETER_LIST]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Matlab/ParameterList/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.MATLAB_PARAMETER_ADD]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Matlab/ParameterAdd/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.MATLAB_PARAMETER_UPDATE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Matlab/ParameterUpdate/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.MATLAB_DEVICE_PARAMETERS]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Matlab/DeviceParameters/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.MATLAB_DEVICE_UPDATE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Matlab/DeviceUpdate/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.CREATE_PARAMETER_UPDATE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Device/CreateParameterUpdate/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.INSTALL_ENVIRONMENT]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'Device/Device/InstallEnvironment/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.CREATE_ROLE]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'User/Role/Create/', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  [APIEvent.DISABLE_INSTALL_CHASERS]: (_, options) => {
    return new Promise((resolve, reject) => {
      api.request('post', 'ECommerce/Order/UpdateChaserStatus', options)
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  // CLEARS SUPPLIED PROPERTY
  [APIEvent.CLEAR]: ({commit}, clearData) => {
    commit(clearData.prop, clearData.value)
  },

  // Auth0 actions
  INITIALIZE_AUTH0: async ({ commit }, { domain, clientId, redirectUri, audience }) => {
    try {
      commit('SET_AUTH0_LOADING', true)
      commit('SET_AUTH0_ERROR', null)
      
      const { createAuth0Client } = await import('@auth0/auth0-spa-js')
      
      const auth0Client = await createAuth0Client({
        domain,
        clientId,
        authorizationParams: {
          audience,
          redirect_uri: redirectUri,
        },
      })
      
      commit('SET_AUTH0_CLIENT', auth0Client)
      
      // Only handle redirect callback if we're actually on the callback URL
      const search = window.location.search
      const isCallback = search.includes("code=") && search.includes("state=")
      
      if (isCallback) {
        try {
          const { appState } = await auth0Client.handleRedirectCallback()
          return appState
        } catch (callbackError) {
          console.error('Auth0 callback handling failed:', callbackError)
          // Don't throw here, just return null so the app can continue
          return null
        }
      }
      
      // Get authentication status and user (only if not on callback)
      const isAuthenticated = await auth0Client.isAuthenticated()
      const user = await auth0Client.getUser()
      
      commit('SET_AUTH0_AUTHENTICATED', isAuthenticated)
      commit('SET_AUTH0_USER', user)
      
      return { isAuthenticated, user }
    } catch (error) {
      commit('SET_AUTH0_ERROR', error)
      throw error
    } finally {
      commit('SET_AUTH0_LOADING', false)
    }
  },

  AUTH0_LOGIN: async ({ state }, options = {}) => {
    if (!state.auth0Client) {
      throw new Error('Auth0 client not initialized')
    }
    return state.auth0Client.loginWithRedirect(options)
  },

  AUTH0_LOGOUT: async ({ state }, options = {}) => {
    if (!state.auth0Client) {
      throw new Error('Auth0 client not initialized')
    }
    return state.auth0Client.logout(options)
  },

  AUTH0_GET_TOKEN: async ({ state }, options = {}) => {
    if (!state.auth0Client) {
      throw new Error('Auth0 client not initialized')
    }
    return state.auth0Client.getTokenSilently(options)
  }
} // end export
